/**
 * ReadOnlyBanner — dashboard-top banner shown to members currently under
 * a 24-hour read-only soft-ban.
 *
 * The banner is intentionally direct: it names the reason, shows how many
 * hours remain, and links to the community guidelines so the member has a
 * one-click path to review the rules. We poll the backend `/auth/me` on
 * mount to catch auto-lifted holds.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ShieldAlert, ExternalLink } from 'lucide-react';

const readableDuration = (untilIso) => {
  if (!untilIso) return '';
  try {
    const ms = new Date(untilIso).getTime() - Date.now();
    if (ms <= 0) return 'window closing';
    const hrs = Math.floor(ms / 3_600_000);
    const mins = Math.floor((ms % 3_600_000) / 60_000);
    if (hrs > 0)  return `${hrs}h ${mins}m remaining`;
    if (mins > 0) return `${mins}m remaining`;
    return 'less than a minute remaining';
  } catch {
    return '';
  }
};

const ReadOnlyBanner = ({ user }) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (user?.status !== 'read_only') return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 60_000); // refresh countdown each minute
    return () => clearInterval(id);
  }, [user?.status]);

  const remaining = useMemo(
    () => readableDuration(user?.read_only_until),
    [user?.read_only_until, tick],
  );

  if (user?.status !== 'read_only') return null;

  return (
    <div
      className="mb-4 rounded-2xl border-2 border-rose-500/40 bg-rose-500/10 px-4 py-3 flex items-start gap-3"
      data-testid="read-only-banner"
      role="alert"
    >
      <div className="h-9 w-9 rounded-xl bg-rose-500/20 text-rose-700 flex items-center justify-center flex-shrink-0">
        <ShieldAlert className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700">
          Your account is on a temporary read-only hold
        </div>
        <div className="mt-0.5 text-sm text-foreground font-semibold">
          You can browse, but you cannot post, comment, or DM for 24 hours.
        </div>
        <div className="mt-1 text-xs text-muted-foreground leading-relaxed">
          <span className="font-semibold text-foreground">Reason:</span>{' '}
          {user.read_only_reason || 'Community guideline violation.'}
          {remaining && <> · <span className="font-semibold text-foreground">{remaining}</span></>}
        </div>
        <div className="mt-2">
          <a
            href="/community/guidelines"
            className="inline-flex items-center gap-1 text-xs font-bold text-rose-700 underline hover:text-rose-800"
          >
            Review community guidelines <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default ReadOnlyBanner;
